import { Component, DestroyRef, inject, linkedSignal, OnInit, signal, ViewChild } from '@angular/core';
import {
  API,
  AssetFileDTO,
  mm_assets_Asset_retrieveSharedAssets_order,
  Order,
  SharedAssetFullDTO,
  SharedAssetRepositoryDTO,
} from '../../../../services/api/api';
import { ModalNavigationService } from '../../../../services/gui/modal/modal-navigation.service';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { ActivatedRoute } from '@angular/router';
import { UtilService } from '../../../../services/util.service';
import { ASSET_CONVERSION_MODE, AssetConversionModalOptions } from '../asset-conversion-modal/asset-conversion-modal.model';
import { AssetConversionModalComponent } from '../asset-conversion-modal/asset-conversion-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService } from '@ngx-seatback/utils';
import { RoutingID } from 'src/app/routes/sb-routing-enum';
import { RequestClass } from '../../../../services/global/RequestClass';
import { FilterPanelComponent, FilterPanelConfig } from '@ngx-seatback/layout';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { OrderOption, SbSplitButton, SplitButtonClass } from '../../../../services/global/global-interfaces';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StringTransformationService } from '../../../../services/transformation-helper/string-transformation.service';
import { DateTransformationService } from '../../../../services/transformation-helper/date-transformation.service';

type SplitButtonAction = 'details';
type SharedAssetFullDTOAndTagsAndButtons = SplitButtonClass<SharedAssetFullDTO, SplitButtonAction>;

@Component({
  selector: 'sb-page-asset-explorer',
  templateUrl: './asset-explorer.component.html',
  styleUrls: ['./asset-explorer.component.scss'],
  standalone: false,
})
export class AssetExplorerComponent extends RequestClass implements OnInit {
  // injects
  protected readonly modalNavigationService = inject(ModalNavigationService);
  protected readonly modalService = inject(ModalService);
  protected readonly api = inject(API);
  protected readonly modal = inject(ModalService);
  protected readonly route = inject(ActivatedRoute);
  protected readonly translate = inject(TranslateService);
  protected readonly navService = inject(NavigationService);
  protected readonly util = inject(UtilService);
  protected readonly destroyRef = inject(DestroyRef);
  private readonly stringTransformation = inject(StringTransformationService);
  protected readonly dateTransformation = inject(DateTransformationService);

  // global reference imports
  protected readonly window = window;
  @ViewChild('FilterPanelRef') filterPanelRef: FilterPanelComponent;
  protected readonly mm_assets_Asset_retrieveSharedAssets_order = mm_assets_Asset_retrieveSharedAssets_order;

  /* filter variables */
  // search
  search: string | null = null;
  exactMatch: SearchOptions = 'contains';
  // Id filtering
  filteredRepositories: SharedAssetRepositoryDTO[] = [];
  filteredTags: string[] = [];

  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    button: { visibleWithoutChanges: true },
  };

  /* ordering */
  orderOption = signal<OrderOption<mm_assets_Asset_retrieveSharedAssets_order>>({
    direction: Order.asc,
    value: mm_assets_Asset_retrieveSharedAssets_order.NAME,
  });
  filterPanelOrderOption = linkedSignal<
    OrderOption<mm_assets_Asset_retrieveSharedAssets_order>,
    OrderOption<mm_assets_Asset_retrieveSharedAssets_order>
  >({
    source: this.orderOption,
    computation: orderOption => {
      return { ...orderOption };
    },
  });
  allOrderOptions: OrderOption<mm_assets_Asset_retrieveSharedAssets_order>[] = [
    { value: mm_assets_Asset_retrieveSharedAssets_order.NAME, direction: Order.desc, label: this.translate.instant('lang.globals.name') },
    {
      value: mm_assets_Asset_retrieveSharedAssets_order.CREATED_AT,
      direction: Order.desc,
      label: this.translate.instant('lang.globals.createdAt'),
    },
    { value: mm_assets_Asset_retrieveSharedAssets_order.ID, direction: Order.desc, label: this.translate.instant('lang.globals.id') },
    {
      value: mm_assets_Asset_retrieveSharedAssets_order.SHARE_END,
      direction: Order.desc,
      label: this.translate.instant('lang.globals.sharedTill'),
    },
    {
      value: mm_assets_Asset_retrieveSharedAssets_order.MODIFIED_AT,
      direction: Order.desc,
      label: this.translate.instant('lang.globals.modifiedAt'),
    },
  ];

  /* global */
  showSideBar: boolean = false;
  assets = signal<SharedAssetFullDTOAndTagsAndButtons[]>([]);
  selectedAsset: SharedAssetFullDTOAndTagsAndButtons | null = null;

  constructor() {
    super();
    this.resetOrderOption();
  }

  ngOnInit(): void {}

  // order methods
  resetOrderOption() {
    // reset to default ordering, copy object properties to avoid changing original default
    this.orderOption.set({ ...this.allOrderOptions[0] });
  }

  setToOrderAndRefresh(pValue: mm_assets_Asset_retrieveSharedAssets_order) {
    this.setToOrderOption(pValue);
    this.requestDebounced();
  }

  setToOrderFilterPanel(pValue: mm_assets_Asset_retrieveSharedAssets_order) {
    this.filterPanelOrderOption.update(orderOption => {
      orderOption.value = pValue;
      return { ...orderOption };
    });
    if (this.filterPanelRef) {
      this.filterPanelRef.hasExternalChanges.set(true);
    }
  }

  setOrderDirectionFilterPanel(value: Order) {
    this.filterPanelOrderOption.update(orderOption => {
      orderOption.direction = value;
      return { ...orderOption };
    });
    if (this.filterPanelRef) {
      this.filterPanelRef.hasExternalChanges.set(true);
    }
  }

  private setToOrderOption(pValue: mm_assets_Asset_retrieveSharedAssets_order) {
    if (!pValue) {
      // nothing is set fallback to default
      this.resetOrderOption();
      return;
    }
    const currentOrderOption = this.orderOption();
    if (currentOrderOption.value === pValue) {
      if (currentOrderOption.direction === Order.asc) {
        this.orderOption.set({ ...currentOrderOption, direction: Order.desc });
      } else {
        this.orderOption.set({ ...currentOrderOption, direction: Order.asc });
      }
    } else {
      this.orderOption.set({ ...this.allOrderOptions.find(option => (option.value = pValue)) });
    }
  }

  getOrder(): { by: mm_assets_Asset_retrieveSharedAssets_order; direction: Order } {
    // if no orderOption -> return default order
    const currentOrderOption = this.orderOption();
    if (!currentOrderOption.value) {
      this.resetOrderOption();
      return {
        by: mm_assets_Asset_retrieveSharedAssets_order.NAME,
        direction: Order.desc,
      };
    }
    return { by: currentOrderOption.value, direction: currentOrderOption.direction };
  }

  // request
  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const repositories = this.filteredRepositories?.length > 0 ? this.filteredRepositories.map(cat => cat?.id) : null;
    const sub = this.api
      .mm_assets_Asset_retrieveSharedAssets(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          repository_id: repositories,
          tags: this.filteredTags?.length > 0 ? this.filteredTags : null,
          order: this.getOrder(),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe((data: SharedAssetFullDTO[]) => {
        this.assets.set(this.buildSplitButtons(data));
      });
    this.subs.push(sub);
  }

  buildSplitButtons<
    T extends {
      id: any;
    },
  >(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      item.sbButtons.push({
        icon: 'details',
        tooltip: 'lang.globals.details',
        action: 'details',
      });
    });
    return result;
  }

  resolveSplitButtonAction(group: SharedAssetFullDTOAndTagsAndButtons, $event: SplitButtonAction) {
    switch ($event) {
      case 'details':
        this.editAsset(group);
        break;

      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  toggleSideBar(): void {
    this.showSideBar = !this.showSideBar;
  }

  editAsset(asset: SharedAssetFullDTO): void {
    this.selectedAsset = asset;
    this.toggleSideBar();
  }

  // conversion methods
  convertToMedium(selectedAssetFile: AssetFileDTO): void {
    this.showConversionModal(selectedAssetFile, 'ASSET_FILE_TO_MEDIUM');
  }

  convertAllToMedia(selectedAsset: SharedAssetFullDTO): void {
    // TODO Trigger check if motive already exists
    // TODO Asset to media, possible mediumgroup?
    this.showConversionModal(selectedAsset, 'ASSET_TO_MEDIUM_GROUP');
  }

  showConversionModal(pObject: AssetFileDTO | SharedAssetFullDTO, pMode: ASSET_CONVERSION_MODE): void {
    const conversionOptions: AssetConversionModalOptions = {
      mode: pMode,
      obj: pObject,
    };
    let modalTitle: string = '';

    switch (pMode) {
      case 'ASSET_FILE_TO_MEDIUM':
        modalTitle = this.translate.instant('lang.assets.convertAssetFileToMedium');
        break;
      case 'ASSET_TO_MEDIUM_GROUP':
        modalTitle = this.translate.instant('lang.assets.convertAssetToMediumGroup');
        break;
    }

    this.modal
      .showNavigationModal({
        entryComponent: {
          value: AssetConversionModalComponent,
          data: conversionOptions,
        },
        title: modalTitle,
        modalOptions: { size: 'lg' },
      })
      .then((result: any) => {
        switch (pMode) {
          case 'ASSET_FILE_TO_MEDIUM':
            this.navService.navigateToPoint(RoutingID.mediumExplorer);
            break;
          case 'ASSET_TO_MEDIUM_GROUP':
            this.navService.navigateToPoint(RoutingID.mediumGroupEdit, { id: 'mediumGroupID', value: result.id });
            break;
        }
      })
      .catch(() => {});
  }

  // filter panel methods
  checkExpansion() {
    return this.filterPanelRef?.isPanelExtended() ?? false;
  }

  openFilterPanel(elementId: HTMLDivElement) {
    if (!elementId) {
      throw new Error('Missing elementId for filter panel opening');
    }
    if (this.filterPanelRef) {
      this.filterPanelRef.isPanelExtended.set(true);
      this.scrollToFilterPanelElement(elementId, false);
    }
  }

  scrollToFilterPanelElement(element: HTMLDivElement, isInitiallyExpanded: boolean = true) {
    if (!element) {
      throw new Error('Missing elementId for filter panel opening');
    }
    const timeout = setTimeout(
      () => {
        const scrollContainer = element.closest('.custom-scrollbar') as HTMLElement;

        if (!scrollContainer) {
          console.warn('No scrollable container found for element');
          return;
        }
        const containerTop = scrollContainer.getBoundingClientRect().top;
        const elementTop = element.getBoundingClientRect().top;

        const headerOffset = 110; // height filter header + height of navbar + offset
        const offsetPosition = elementTop - containerTop + scrollContainer.scrollTop - headerOffset;

        scrollContainer.scrollTo({ behavior: 'smooth', top: offsetPosition });
        // elementId.scrollBy({ top: 40 });
        clearTimeout(timeout);
      },
      isInitiallyExpanded ? 100 : 400
    );
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.filteredRepositories = filterMap.get('repositories');
    this.filteredTags = filterMap.get('tags');
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
    this.orderOption.set({ ...this.filterPanelOrderOption() });
  }
}
